import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  createItem,
  getItemStatuses,
  uploadItemImage,
  addItemImage,
} from '../../../endpoints/chapterSupplies';
import type { CreateItemRequest } from '../../../types/chapterSupplies';

const initialForm: CreateItemRequest = {
  name: '',
  item_status_id: undefined,
  description: null,
  sku: null,
  reorder_point: null,
  weight: null,
  length: null,
  width: null,
  height: null,
};

export default function ItemCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateItemRequest>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const itemImageInputRef = useRef<HTMLInputElement>(null);

  const { data: statuses = [] } = useApiGet(
    ['chapter-supplies-statuses-items'],
    getItemStatuses,
    { enabled: true, retry: 1 }
  );

  const { mutate: submitItem, isPending } = useApiSend(
    async (params: { data: CreateItemRequest; imageURL: string | null }) => {
      const created = await createItem(params.data);
      if (params.imageURL?.trim()) {
        await addItemImage(created.id, {
          image_url: params.imageURL.trim(),
          sort_order: 0,
        });
      }
      return created;
    },
    (response) => {
      navigate(`/chapter-supplies-management/items/${response.id}`);
    },
    (err) => {
      setError(err?.message ?? 'Failed to create item');
    },
    [['chapter-supplies-items']]
  );

  const handleChange = (
    field: keyof CreateItemRequest,
    value: string | number | null | undefined
  ) => {
    setForm((prev) => ({ ...prev, [field]: value ?? null }));
    setError(null);
  };

  const handleItemImageFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImageUploading(true);
    setError(null);
    try {
      const { url } = await uploadItemImage(file);
      const trimmed = url?.trim() ?? '';
      if (!trimmed) {
        throw new Error('Upload did not return a URL');
      }
      setPendingImageUrl(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      setError('Name is required');
      return;
    }
    const payload: CreateItemRequest = {
      name: form.name.trim(),
      item_status_id: form.item_status_id?.trim() || undefined,
      description: form.description?.trim() || null,
      sku: form.sku?.trim() || null,
      reorder_point: form.reorder_point != null ? form.reorder_point : null,
      weight: form.weight != null ? form.weight : null,
      length: form.length != null ? form.length : null,
      width: form.width != null ? form.width : null,
      height: form.height != null ? form.height : null,
    };
    submitItem({ data: payload, imageURL: pendingImageUrl });
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Create item"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management/items')}
            >
              Back to list
            </Button>
          }
          rightDom={undefined}
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name ?? ''}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!error && !form.name?.trim()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="item-status-label">Status</InputLabel>
                <Select
                  labelId="item-status-label"
                  label="Status"
                  value={form.item_status_id ?? ''}
                  onChange={(e) =>
                    handleChange('item_status_id', e.target.value || undefined)
                  }
                >
                  <MenuItem value="">Draft (default)</MenuItem>
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={form.description ?? ''}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Image (uploaded file will be attached after the item is created)
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 0.5,
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  ref={itemImageInputRef}
                  onChange={handleItemImageFileChange}
                />
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  startIcon={<AddPhotoAlternate />}
                  disabled={imageUploading || isPending}
                  onClick={() => itemImageInputRef.current?.click()}
                >
                  {imageUploading ? 'Uploading…' : 'Upload image'}
                </Button>
                {pendingImageUrl ? (
                  <Button
                    type="button"
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutline />}
                    disabled={isPending}
                    onClick={() => setPendingImageUrl(null)}
                  >
                    Remove
                  </Button>
                ) : null}
                {pendingImageUrl ? (
                  <Box
                    component="img"
                    src={pendingImageUrl}
                    alt=""
                    sx={{
                      maxHeight: 100,
                      maxWidth: 160,
                      borderRadius: 1,
                      objectFit: 'contain',
                    }}
                  />
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                value={form.sku ?? ''}
                onChange={(e) => handleChange('sku', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Reorder point"
                value={form.reorder_point ?? ''}
                onChange={(e) =>
                  handleChange(
                    'reorder_point',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Weight"
                value={form.weight ?? ''}
                onChange={(e) =>
                  handleChange(
                    'weight',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Length"
                value={form.length ?? ''}
                onChange={(e) =>
                  handleChange(
                    'length',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Width"
                value={form.width ?? ''}
                onChange={(e) =>
                  handleChange(
                    'width',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Height"
                value={form.height ?? ''}
                onChange={(e) =>
                  handleChange(
                    'height',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={isPending || !form.name?.trim()}
              >
                {isPending ? 'Creating…' : 'Create item'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}
